import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Soulprint", href: "/quiz" },
  { label: "Experiences", href: "/experiences" },
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
  { label: "EMI & Financing", href: "/emi" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar({ onPlanJourney }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const location = useLocation();

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      if (current < 80) { setVisible(true); }
      else if (current > lastScrollY.current + 8) { setVisible(false); setMenuOpen(false); }
      else if (current < lastScrollY.current - 8) { setVisible(true); }
      lastScrollY.current = current;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  // setVisible, setMenuOpen are stable React setters; lastScrollY is a ref — empty deps correct
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const desktopLinkClass = (href) =>
    `text-[10px] xl:text-xs uppercase tracking-[0.18em] transition-colors duration-200 whitespace-nowrap ${
      location.pathname === href ? "text-[#C9A84C]" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
    }`;

  const mobileTileClass = (active) =>
    `text-xs font-semibold uppercase tracking-[0.22em] px-4 py-2.5 border rounded-sm transition-all duration-200 min-w-[72px] text-center ${
      active
        ? "border-[#C9A84C]/60 text-[#C9A84C] bg-[#C9A84C]/5"
        : "border-white/25 text-white/85 hover:border-[#C9A84C]/40 hover:text-[#C9A84C]"
    }`;

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-transform duration-300"
      style={{ transform: visible ? "translateY(0)" : "translateY(-100%)" }}
    >
      {/* Desktop: solid background */}
      <div className="hidden lg:block absolute inset-0 bg-black/95 backdrop-blur-xl border-b border-white/5" />

      {/* Mobile: subtle top gradient for readability only */}
      <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-black/55 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-10 flex items-center" style={{ height: 64 }}>

        {/* Mobile: HOME tile button (left) */}
        <Link
          to="/"
          data-testid="nav-link-home"
          className={`lg:hidden mr-auto ${mobileTileClass(location.pathname === "/")}`}
        >
          HOME
        </Link>

        {/* Desktop: HOME text link (left) */}
        <Link
          to="/"
          data-testid="nav-link-home-desktop"
          className={`hidden lg:block mr-auto font-medium ${desktopLinkClass("/")}`}
        >
          HOME
        </Link>

        {/* Desktop nav links + CTA */}
        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {NAV_LINKS.map(link => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              className={desktopLinkClass(link.href)}
            >
              {link.label}
            </Link>
          ))}
          <button
            data-testid="navbar-plan-journey-btn"
            onClick={onPlanJourney}
            className="btn-gold text-[10px] whitespace-nowrap ml-2"
          >
            Design My Journey
          </button>
        </div>

        {/* Mobile: MENU tile button (right) — symmetrical with HOME */}
        <button
          data-testid="navbar-menu-toggle"
          className={`lg:hidden ml-auto ${mobileTileClass(menuOpen)}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? "CLOSE" : "MENU"}
        </button>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden border-t border-white/10 px-5 py-6 flex flex-col gap-4"
          style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(24px)" }}
        >
          {NAV_LINKS.map(link => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className={`text-sm uppercase tracking-[0.2em] py-1 transition-colors ${
                location.pathname === link.href ? "text-[#C9A84C]" : "text-[#A1A1AA] hover:text-[#C9A84C]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            data-testid="navbar-mobile-plan-btn"
            onClick={() => { setMenuOpen(false); onPlanJourney && onPlanJourney(); }}
            className="btn-gold w-full mt-3 py-3.5"
          >
            Design My Journey
          </button>
        </div>
      )}
    </nav>
  );
}
